import oakScreens, {
  oakComponentProductGallery,
  oakDesignDevelopmentGallery,
  oakGovernanceGallery,
} from "../../oakMedia";

const oak = {
  id: "oak",
  slug: "oak",
  category: "Foundations",
  navTitle: "Building the Oak Design System",
  title: "Building the Oak Design System",
  introduction: [
    "Co-operators was undertaking a major rebrand and digital transformation across products built in React, Vue, and Angular. I led the creation and rollout of Oak, a shared design system that became a foundation for the organization’s digital ecosystem.",
  ],
  challenge: [
    {
      title: "Fragmented experiences",
      paragraphs: [
        "Teams were solving similar interface problems independently, which led to inconsistent branding, duplicated effort, and uneven customer experiences.",
      ],
    },
    {
      title: "A print-first brand",
      paragraphs: [
        "The new brand had been developed mainly for traditional media. It needed to work across responsive products, interaction states, accessibility requirements, and both light and dark experiences.",
      ],
    },
    {
      title: "Enterprise scale",
      paragraphs: [
        "Oak had to support several products and technology frameworks without becoming rigid or creating another central bottleneck.",
      ],
    },
  ],
  process: [
    {
      title: "Building the foundation",
      paragraphs: [
        "I partnered with XDS and internal design and engineering teams to establish the structure of the system.",
      ],
      bullets: [
        "Used atomic design principles to create a modular component foundation.",
        "Defined shared patterns for colour, typography, spacing, behaviour, and interaction.",
        "Built support for React, Vue, and Angular so teams could adopt Oak without replacing their existing stacks.",
        "Worked closely with developers to make sure the design intent and implementation stayed aligned.",
      ],
      closing:
        "The goal was not to create a library for one product. It was to solve recurring enterprise needs once and make those solutions reusable.",
    },
    {
      title: "Embedding accessibility",
      paragraphs: [
        "Accessibility was part of the component lifecycle from the beginning.",
      ],
      bullets: [
        "Reviewed every component for usability and WCAG compliance.",
        "Adapted the print-focused colour palette for accessible digital use.",
        "Supported both light and dark modes.",
        "Worked with QA and development teams to address issues at the shared-component level.",
        "Monitored where components were being used so testing effort could be focused where it mattered most.",
      ],
      closing:
        "Fixing an issue in Oak meant improving every product that used the same component.",
    },
    {
      title: "Supporting adoption",
      paragraphs: [
        "The system needed to operate as a product, not as a one-time design exercise.",
      ],
      bullets: [
        "Created documentation, versioning, and release notes.",
        "Delivered workshops and training for designers and developers.",
        "Established feedback and feature-request channels.",
        "Created contribution and governance practices so teams could help shape the system without weakening consistency.",
      ],
    },
  ],
  outcomes: [
    {
      title: "A shared digital foundation",
      description:
        "Oak became a common foundation across products and platforms, improving consistency in brand, interaction, and accessibility.",
    },
    {
      title: "Faster design and delivery",
      description:
        "Designers could create production-ready layouts directly in Figma using components aligned with the implemented system. This contributed to a 50% reduction in design time and a 30% reduction in development time.",
    },
    {
      title: "Accessibility at scale",
      description:
        "Teams could use tested, accessible patterns rather than solving the same issues repeatedly within individual products.",
    },
    {
      title: "Stronger collaboration",
      description:
        "Oak gave design, development, QA, and product teams a shared language and reduced ambiguity during delivery.",
    },
  ],
  reflection: [
    "Oak reinforced that a design system is not primarily a set of components. The components matter, but the value comes from the operating model around them.",
    "That includes governance, accessibility, documentation, testing, contribution, and shared ownership. Oak succeeded because it reduced repeated decisions while still giving teams enough flexibility to solve real product problems.",
  ],
  media: {
    blocks: [
      {
        id: "oak-system-composition",
        type: "composition",
        placement: "afterIntro",
        className: "work-page__media-block--integrated-opening",
        items: oakScreens,
        aspectRatio: "3 / 2",
        label: "Oak design system documentation across system architecture, components, and implementation guidance",
        galleryLabel: "Oak design system foundation gallery",
        alt: "Oak design system overview, component library, and implementation documentation",
      },
      {
        id: "oak-design-development",
        type: "grid",
        placement: "afterChallenge",
        title: "One foundation for design and development",
        description:
          "Shared resources and implementation guidance gave designers and developers a common starting point while supporting the organization’s existing technology stacks.",
        layout: "default",
        options: {
          maxWidth: 1000,
          tileMin: 320,
          thumbHeight: 420,
          thumbFit: "contain",
          modalMaxWidth: 1180,
          modalMaxHeight: "min(90vh, 960px)",
        },
        items: oakDesignDevelopmentGallery,
      },
      {
        id: "oak-components-in-product",
        type: "grid",
        placement: "afterApproach",
        title: "Documented standards, tested in product",
        description:
          "Component guidance connected interaction and accessibility decisions to implementation. Applying Oak within the authenticated client portal tested whether those standards could withstand real enterprise complexity.",
        layout: "default",
        options: {
          maxWidth: 1000,
          tileMin: 280,
          thumbHeight: 360,
          thumbFit: "contain",
          modalMaxWidth: 1180,
          modalMaxHeight: "min(90vh, 960px)",
        },
        items: oakComponentProductGallery,
      },
      {
        id: "oak-governance",
        type: "grid",
        placement: "afterOutcomes",
        title: "A system designed to evolve",
        description:
          "Roadmapping and visible release history made Oak an operating product rather than a static component library.",
        layout: "default",
        options: {
          maxWidth: 1000,
          tileMin: 320,
          thumbHeight: 400,
          thumbFit: "contain",
          modalMaxWidth: 1180,
          modalMaxHeight: "min(90vh, 960px)",
        },
        items: oakGovernanceGallery,
      },
    ],
  },
};

export default oak;
